// v3.0.0-rc.4 — Phase 5.b.4 — Governance Dashboard (Human Review)
// Layout autonome banking-pro. Nomenclature neutre : "Governance Dashboard"
// (zéro mention AYA / Alegria visible en UI).
// Accès strict : RequireAuth + check role === 'owner' côté page.
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  ListChecks,
  ScrollText,
  AlertTriangle,
  SlidersHorizontal,
  BarChart3,
  Sun,
  Moon,
  Menu,
  X,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./Governance.css";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────
function _authHeaders() {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ..._authHeaders(),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({ ok: false, error: "invalid_json" }));
  if (!res.ok) {
    const err = new Error(data?.error || `http_${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function shortHash(h) {
  if (!h || typeof h !== "string") return "—";
  return h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h;
}

// ────────────────────────────────────────────────────────────────────────
// Owner guard (page-level)
// ────────────────────────────────────────────────────────────────────────
function useOwnerGuard() {
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, isOwner: false, user: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api("/api/auth/me");
        if (cancelled) return;
        if (me?.user?.role !== "owner") {
          setState({ checking: false, isOwner: false, user: me?.user || null });
          // Soft redirect — non-owner ne reste pas sur /governance.
          setTimeout(() => navigate("/", { replace: true }), 1500);
        } else {
          setState({ checking: false, isOwner: true, user: me.user });
        }
      } catch (e) {
        if (cancelled) return;
        if (e.status === 401) {
          navigate("/login?next=%2Fgovernance", { replace: true });
        } else {
          setState({ checking: false, isOwner: false, user: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);
  return state;
}

// ────────────────────────────────────────────────────────────────────────
// Tabs config
// ────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "reviews", label: "Human Reviews", icon: ShieldCheck },
  { id: "log", label: "Decision Log", icon: ListChecks },
  { id: "compliance", label: "AI Act Compliance", icon: ScrollText },
  { id: "risk", label: "Risk Analysis", icon: AlertTriangle },
  { id: "policy", label: "Policy Engine", icon: SlidersHorizontal },
  { id: "analytics", label: "Decision Analytics", icon: BarChart3 },
];

// ────────────────────────────────────────────────────────────────────────
// Sub-views
// ────────────────────────────────────────────────────────────────────────
function OverviewTab() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verify, setVerify] = useState({ running: false, result: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v3/governance/overview");
      setKpis(r.kpis || {});
    } catch (e) {
      setKpis({ error: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runVerify = async () => {
    setVerify({ running: true, result: null });
    try {
      const r = await api("/api/v3/governance/chain-verify");
      setVerify({ running: false, result: r });
    } catch (e) {
      setVerify({ running: false, result: { ok: false, error: e.message } });
    }
  };

  const cards = [
    { k: "pending", label: "Pending review", hint: "À traiter maintenant" },
    { k: "stale_escalated_24h", label: "Stale > 24h", hint: "En attente depuis +24h" },
    { k: "approved_7d", label: "Approved · 7j", hint: "Décisions APPROVED" },
    { k: "rejected_7d", label: "Rejected · 7j", hint: "Décisions REJECTED" },
    { k: "total_entries", label: "Total entries", hint: "Volume cumulé" },
    { k: "avg_confidence_30d", label: "Avg confidence · 30j", hint: "Score moyen JDON", isPct: true },
  ];

  return (
    <div className="gov-pane" data-testid="gov-overview-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Overview</h2>
        <button className="gov-btn gov-btn-ghost" onClick={refresh} disabled={loading} data-testid="gov-overview-refresh">
          <RefreshCw size={16} /> Rafraîchir
        </button>
      </div>

      {loading && <div className="gov-skeleton">Chargement…</div>}

      {!loading && kpis && !kpis.error && (
        <div className="gov-kpi-grid" data-testid="gov-kpi-grid">
          {cards.map((c) => {
            const v = kpis[c.k];
            let display = "—";
            if (v !== null && v !== undefined) {
              display = c.isPct
                ? `${(v * 100).toFixed(1)}%`
                : new Intl.NumberFormat("fr-FR").format(v);
            }
            return (
              <div className="gov-kpi-card" key={c.k} data-testid={`gov-kpi-${c.k}`}>
                <div className="gov-kpi-label">{c.label}</div>
                <div className="gov-kpi-value">{display}</div>
                <div className="gov-kpi-hint">{c.hint}</div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && kpis?.error && (
        <div className="gov-error">Erreur de chargement : {kpis.error}</div>
      )}

      <div className="gov-card">
        <div className="gov-card-head">
          <div>
            <div className="gov-card-title">Chain integrity</div>
            <div className="gov-card-sub">Vérifie la chaîne de hash JSONFIRST (audit immuable).</div>
          </div>
          <button
            className="gov-btn gov-btn-primary"
            onClick={runVerify}
            disabled={verify.running}
            data-testid="gov-chain-verify-btn"
          >
            {verify.running ? <RefreshCw size={16} className="gov-spin" /> : <ShieldCheck size={16} />}
            Vérifier l&apos;intégrité
          </button>
        </div>
        {verify.result && (
          <pre className="gov-pre" data-testid="gov-chain-verify-result">
            {JSON.stringify(verify.result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function ReviewModal({ entry, onClose, onDone }) {
  const [decision, setDecision] = useState("APPROVED");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const requireComment = decision === "REJECTED";
  const canSubmit = !submitting && (!requireComment || comment.trim().length >= 10);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api(`/api/v3/governance/decide/${encodeURIComponent(entry.entry_id)}`, {
        method: "POST",
        body: JSON.stringify({ decision, comment: comment.trim() }),
      });
      onDone(r);
    } catch (e) {
      setError(e.body?.hint || e.body?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gov-modal-backdrop" onClick={onClose} data-testid="gov-review-modal">
      <div className="gov-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gov-modal-head">
          <div>
            <div className="gov-modal-title">Réviser la décision</div>
            <div className="gov-modal-sub">entry_id : <code>{entry.entry_id}</code></div>
          </div>
          <button className="gov-icon-btn" onClick={onClose} aria-label="Fermer" data-testid="gov-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="gov-modal-body">
          <div className="gov-meta-grid">
            <div><span className="gov-meta-k">User</span><span className="gov-meta-v">{entry.user_id || "—"}</span></div>
            <div><span className="gov-meta-k">Profession</span><span className="gov-meta-v">{entry.profession || "—"}</span></div>
            <div><span className="gov-meta-k">Mode</span><span className="gov-meta-v">{entry.mode || "—"}</span></div>
            <div><span className="gov-meta-k">Verdict</span><span className="gov-meta-v">{entry.verdict_engine || "—"}</span></div>
            <div><span className="gov-meta-k">Confidence</span><span className="gov-meta-v">{entry.confidence != null ? (entry.confidence * 100).toFixed(1) + "%" : "—"}</span></div>
            <div><span className="gov-meta-k">Créée</span><span className="gov-meta-v">{fmtDate(entry.created_at)}</span></div>
          </div>

          {entry.prompt && (
            <div className="gov-block">
              <div className="gov-block-title">Prompt</div>
              <div className="gov-block-body">{entry.prompt}</div>
            </div>
          )}

          {Array.isArray(entry.active_blocks) && entry.active_blocks.length > 0 && (
            <div className="gov-block">
              <div className="gov-block-title">Active blocks ({entry.active_blocks.length})</div>
              <div className="gov-tags">
                {entry.active_blocks.map((b, i) => (
                  <span key={i} className="gov-tag gov-tag-warn">{b}</span>
                ))}
              </div>
            </div>
          )}

          <div className="gov-block">
            <div className="gov-block-title">Décision</div>
            <div className="gov-radio-row">
              <label className={`gov-radio ${decision === "APPROVED" ? "is-active" : ""}`}>
                <input
                  type="radio"
                  name="gov-decision"
                  value="APPROVED"
                  checked={decision === "APPROVED"}
                  onChange={() => setDecision("APPROVED")}
                  data-testid="gov-decision-approve"
                />
                <CheckCircle2 size={16} /> Approuver
              </label>
              <label className={`gov-radio ${decision === "REJECTED" ? "is-active" : ""}`}>
                <input
                  type="radio"
                  name="gov-decision"
                  value="REJECTED"
                  checked={decision === "REJECTED"}
                  onChange={() => setDecision("REJECTED")}
                  data-testid="gov-decision-reject"
                />
                <XCircle size={16} /> Rejeter
              </label>
            </div>
          </div>

          <div className="gov-block">
            <div className="gov-block-title">
              Commentaire {requireComment ? <span className="gov-req">obligatoire · 10 chars min</span> : <span className="gov-opt">optionnel</span>}
            </div>
            <textarea
              className="gov-textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={requireComment ? "Motiver le rejet (audit AI Act)…" : "Note optionnelle pour l'audit…"}
              data-testid="gov-decision-comment"
            />
            {requireComment && (
              <div className="gov-counter">{comment.trim().length} / 10</div>
            )}
          </div>

          {error && <div className="gov-error" data-testid="gov-decide-error">{error}</div>}
        </div>

        <div className="gov-modal-foot">
          <button className="gov-btn gov-btn-ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </button>
          <button
            className={`gov-btn ${decision === "APPROVED" ? "gov-btn-primary" : "gov-btn-danger"}`}
            disabled={!canSubmit}
            onClick={submit}
            data-testid="gov-decision-submit"
          >
            {submitting ? <RefreshCw size={16} className="gov-spin" /> : (decision === "APPROVED" ? <CheckCircle2 size={16} /> : <XCircle size={16} />)}
            Confirmer {decision === "APPROVED" ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewsTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v3/governance/pending?limit=100");
      setEntries(r.entries || []);
    } catch (_) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="gov-pane" data-testid="gov-reviews-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Human Reviews <span className="gov-pill">{entries.length}</span></h2>
        <button className="gov-btn gov-btn-ghost" onClick={refresh} disabled={loading}>
          <RefreshCw size={16} /> Rafraîchir
        </button>
      </div>

      {loading && <div className="gov-skeleton">Chargement…</div>}

      {!loading && entries.length === 0 && (
        <div className="gov-empty" data-testid="gov-reviews-empty">
          Aucune entrée en attente de révision. La chaîne est saine.
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="gov-review-list" data-testid="gov-reviews-list">
          {entries.map((e) => (
            <article key={e.entry_id} className="gov-review-row" data-testid={`gov-review-row-${e.entry_id}`}>
              <div className="gov-review-main">
                <div className="gov-review-head">
                  <span className="gov-badge gov-badge-escalated">ESCALATED</span>
                  <span className="gov-review-id">{e.entry_id}</span>
                  <span className="gov-review-date">{fmtDate(e.created_at)}</span>
                </div>
                <div className="gov-review-prompt">{e.prompt || "(pas de prompt)"}</div>
                <div className="gov-review-meta">
                  <span><b>User :</b> {e.user_id || "—"}</span>
                  <span><b>Profession :</b> {e.profession || "—"}</span>
                  <span><b>Mode :</b> {e.mode || "—"}</span>
                  <span><b>Confidence :</b> {e.confidence != null ? (e.confidence * 100).toFixed(1) + "%" : "—"}</span>
                </div>
              </div>
              <div className="gov-review-actions">
                <button
                  className="gov-btn gov-btn-primary"
                  onClick={() => { setActive({ ...e, _pref: "APPROVED" }); }}
                  data-testid={`gov-approve-${e.entry_id}`}
                >
                  <CheckCircle2 size={16} /> Approve
                </button>
                <button
                  className="gov-btn gov-btn-danger"
                  onClick={() => { setActive({ ...e, _pref: "REJECTED" }); }}
                  data-testid={`gov-reject-${e.entry_id}`}
                >
                  <XCircle size={16} /> Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {active && (
        <ReviewModal
          entry={active}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); refresh(); }}
        />
      )}
    </div>
  );
}

function LogTab() {
  const [filters, setFilters] = useState({ status: "", decision: "" });
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set("limit", String(LIMIT));
    qs.set("skip", String(skip));
    if (filters.status) qs.set("status", filters.status);
    if (filters.decision) qs.set("decision", filters.decision);
    try {
      const r = await api(`/api/v3/governance/log?${qs}`);
      setEntries(r.entries || []);
      setTotal(r.total || 0);
    } catch (_) {
      setEntries([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.decision, skip]);

  useEffect(() => { load(); }, [load]);

  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="gov-pane" data-testid="gov-log-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Decision Log</h2>
        <div className="gov-filters">
          <select
            value={filters.status}
            onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, status: e.target.value })); }}
            data-testid="gov-log-filter-status"
          >
            <option value="">Tous les statuts</option>
            <option value="PENDING">PENDING</option>
            <option value="COMPLETED">COMPLETED</option>
            <option value="AUTO_APPROVED">AUTO_APPROVED</option>
          </select>
          <select
            value={filters.decision}
            onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, decision: e.target.value })); }}
            data-testid="gov-log-filter-decision"
          >
            <option value="">Toutes les décisions</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
          </select>
        </div>
      </div>

      <div className="gov-table-wrap">
        <table className="gov-table" data-testid="gov-log-table">
          <thead>
            <tr>
              <th>entry_id</th>
              <th>Date</th>
              <th>User</th>
              <th>Mode</th>
              <th>Verdict</th>
              <th>Status</th>
              <th>Decision</th>
              <th>Hash</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="gov-td-loading">Chargement…</td></tr>}
            {!loading && entries.length === 0 && <tr><td colSpan={8} className="gov-td-empty">Aucune entrée.</td></tr>}
            {!loading && entries.map((e) => (
              <tr key={e.entry_id}>
                <td><code>{shortHash(e.entry_id)}</code></td>
                <td>{fmtDate(e.created_at)}</td>
                <td>{e.user_id || "—"}</td>
                <td>{e.mode || "—"}</td>
                <td>{e.verdict_engine || "—"}</td>
                <td><span className={`gov-status gov-status-${(e.status || "").toLowerCase()}`}>{e.status || "—"}</span></td>
                <td>{e.decision ? <span className={`gov-status gov-status-${e.decision.toLowerCase()}`}>{e.decision}</span> : "—"}</td>
                <td><code>{shortHash(e.entry_hash)}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="gov-pagination">
        <button
          className="gov-btn gov-btn-ghost"
          disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - LIMIT))}
          data-testid="gov-log-prev"
        >
          <ChevronLeft size={16} /> Précédent
        </button>
        <span className="gov-pagination-info">Page {page} / {pages} · {total} entrées</span>
        <button
          className="gov-btn gov-btn-ghost"
          disabled={skip + LIMIT >= total}
          onClick={() => setSkip(skip + LIMIT)}
          data-testid="gov-log-next"
        >
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function ComplianceTab() {
  // Mapping statique AI Act EU 2024 → contrôles JSONFIRST.
  // Aucune mention "AYA" ou "Alegria" — texte neutre, produit licenciable.
  const ROWS = [
    { article: "Art. 9 — Risk management", ctrl: "JDON verdict engine + RAG context", status: "active" },
    { article: "Art. 10 — Data governance", ctrl: "Schema-validated logs (decision_log, hash chain)", status: "active" },
    { article: "Art. 12 — Record-keeping", ctrl: "Immutable hash chain · HMAC signed entries", status: "active" },
    { article: "Art. 13 — Transparency", ctrl: "User prompt + verdict + active_blocks exposés", status: "active" },
    { article: "Art. 14 — Human oversight", ctrl: "Human Reviews tab · approve/reject signés", status: "active" },
    { article: "Art. 15 — Accuracy & robustness", ctrl: "Confidence score + chain-verify endpoint", status: "active" },
    { article: "Art. 60 — Real-world testing", ctrl: "Decision Log analytics (latency, throughput)", status: "active" },
  ];
  return (
    <div className="gov-pane" data-testid="gov-compliance-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">AI Act Compliance</h2>
        <span className="gov-pill gov-pill-ok">Conforme EU AI Act 2024</span>
      </div>
      <p className="gov-paragraph">
        Mapping entre les articles de l&apos;AI Act (Règlement UE 2024/1689) et les contrôles JSONFIRST en place.
        Chaque entrée du Decision Log est signée (HMAC-SHA256) et chaînée par hash — l&apos;intégrité peut être
        vérifiée à la demande depuis l&apos;onglet Overview.
      </p>
      <div className="gov-table-wrap">
        <table className="gov-table">
          <thead>
            <tr>
              <th>Article AI Act</th>
              <th>Contrôle JSONFIRST</th>
              <th>État</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.article}>
                <td><b>{r.article}</b></td>
                <td>{r.ctrl}</td>
                <td><span className="gov-status gov-status-approved">{r.status.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RiskTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/api/v3/governance/analytics");
        setData(r);
      } catch (_) {
        setData({ ok: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const blocks = data?.top_blocks || [];
  const max = Math.max(1, ...blocks.map((b) => b.count));

  return (
    <div className="gov-pane" data-testid="gov-risk-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Risk Analysis</h2>
      </div>
      <p className="gov-paragraph">
        Top JDONs (active_blocks) déclenchés par le moteur d&apos;évaluation. Ce classement reflète les
        risques les plus fréquemment levés sur la période analysée — utile pour ajuster la Policy.
      </p>
      {loading && <div className="gov-skeleton">Chargement…</div>}
      {!loading && blocks.length === 0 && <div className="gov-empty">Aucun blocage déclenché.</div>}
      {!loading && blocks.length > 0 && (
        <div className="gov-bars">
          {blocks.map((b) => (
            <div key={b._id} className="gov-bar-row">
              <div className="gov-bar-label">{b._id}</div>
              <div className="gov-bar-track">
                <div className="gov-bar-fill" style={{ width: `${(b.count / max) * 100}%` }} />
              </div>
              <div className="gov-bar-count">{b.count}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyTab() {
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/api/v3/governance/catalog");
        setCat(r);
      } catch (e) {
        setCat({ ok: false, error: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="gov-pane" data-testid="gov-policy-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Policy Engine</h2>
      </div>
      <p className="gov-paragraph">
        Catalog des implants, hybrid_modes et professions disponibles côté moteur JSONFIRST.
        Lecture-seule depuis le partenaire — toute évolution passe par un déploiement Policy versionné.
      </p>
      {loading && <div className="gov-skeleton">Chargement…</div>}
      {!loading && cat && (
        <pre className="gov-pre" data-testid="gov-policy-catalog">
          {JSON.stringify(cat.catalog || cat, null, 2)}
        </pre>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/api/v3/governance/analytics");
        setData(r);
      } catch (_) {
        setData({ ok: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byMode = data?.by_mode || [];
  const byProf = data?.by_profession || [];
  const lat = data?.review_latency || {};

  const fmtLatency = (ms) => {
    if (!ms) return "—";
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    if (ms < 3600_000) return `${(ms / 60_000).toFixed(1)} min`;
    return `${(ms / 3600_000).toFixed(1)} h`;
  };

  return (
    <div className="gov-pane" data-testid="gov-analytics-pane">
      <div className="gov-pane-header">
        <h2 className="gov-h2">Decision Analytics</h2>
      </div>
      {loading && <div className="gov-skeleton">Chargement…</div>}
      {!loading && (
        <>
          <div className="gov-kpi-grid">
            <div className="gov-kpi-card">
              <div className="gov-kpi-label">Avg review latency</div>
              <div className="gov-kpi-value">{fmtLatency(lat.avg_ms)}</div>
              <div className="gov-kpi-hint">{lat.n || 0} décisions complétées</div>
            </div>
            <div className="gov-kpi-card">
              <div className="gov-kpi-label">Distinct modes</div>
              <div className="gov-kpi-value">{byMode.length}</div>
              <div className="gov-kpi-hint">Modes actifs sur la période</div>
            </div>
            <div className="gov-kpi-card">
              <div className="gov-kpi-label">Distinct professions</div>
              <div className="gov-kpi-value">{byProf.length}</div>
              <div className="gov-kpi-hint">Professions servies</div>
            </div>
          </div>
          <div className="gov-card">
            <div className="gov-card-title">Volumes par mode</div>
            {byMode.length === 0 ? (
              <div className="gov-empty">Aucune donnée.</div>
            ) : (
              <table className="gov-table">
                <thead><tr><th>Mode</th><th>Volume</th></tr></thead>
                <tbody>
                  {byMode.map((m) => (
                    <tr key={m._id || "null"}><td>{m._id || "—"}</td><td>{m.count}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="gov-card">
            <div className="gov-card-title">Volumes par profession</div>
            {byProf.length === 0 ? (
              <div className="gov-empty">Aucune donnée.</div>
            ) : (
              <table className="gov-table">
                <thead><tr><th>Profession</th><th>Volume</th></tr></thead>
                <tbody>
                  {byProf.map((p) => (
                    <tr key={p._id || "null"}><td>{p._id || "—"}</td><td>{p.count}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────
export default function GovernancePage() {
  const guard = useOwnerGuard();
  const [tab, setTab] = useState("overview");
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("gov_theme") || "dark";
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("gov_theme", theme); } catch (_) { /* noop */ }
  }, [theme]);

  const Active = useMemo(() => {
    switch (tab) {
      case "reviews": return ReviewsTab;
      case "log": return LogTab;
      case "compliance": return ComplianceTab;
      case "risk": return RiskTab;
      case "policy": return PolicyTab;
      case "analytics": return AnalyticsTab;
      default: return OverviewTab;
    }
  }, [tab]);

  if (guard.checking) {
    return (
      <div className={`gov-root gov-theme-${theme}`}>
        <div className="gov-fullscreen-msg" data-testid="gov-loading">Vérification des droits…</div>
      </div>
    );
  }

  if (!guard.isOwner) {
    return (
      <div className={`gov-root gov-theme-${theme}`}>
        <div className="gov-fullscreen-msg" data-testid="gov-forbidden">
          Accès restreint au rôle <code>owner</code>. Redirection en cours…
        </div>
      </div>
    );
  }

  return (
    <div className={`gov-root gov-theme-${theme}`} data-testid="governance-dashboard">
      <header className="gov-header">
        <button
          className="gov-icon-btn gov-only-mobile"
          onClick={() => setDrawerOpen(true)}
          aria-label="Ouvrir le menu"
          data-testid="gov-drawer-open"
        >
          <Menu size={20} />
        </button>
        <div className="gov-brand">
          <ShieldCheck size={18} />
          <span>Governance Dashboard</span>
        </div>
        <div className="gov-header-actions">
          <button
            className="gov-icon-btn"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            aria-label="Basculer thème"
            data-testid="gov-theme-toggle"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      <div className="gov-body">
        <aside className={`gov-sidebar ${drawerOpen ? "is-open" : ""}`} data-testid="gov-sidebar">
          <button
            className="gov-icon-btn gov-drawer-close gov-only-mobile"
            onClick={() => setDrawerOpen(false)}
            aria-label="Fermer le menu"
            data-testid="gov-drawer-close"
          >
            <X size={18} />
          </button>
          <nav className="gov-nav">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  className={`gov-nav-item ${active ? "is-active" : ""}`}
                  onClick={() => { setTab(t.id); setDrawerOpen(false); }}
                  data-testid={`gov-tab-${t.id}`}
                >
                  <Icon size={16} />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="gov-sidebar-foot">
            <div className="gov-foot-line">v3.0.0-rc.4 · Phase 5.b.4</div>
            <div className="gov-foot-line gov-foot-dim">Owner : {guard.user?.phone || "—"}</div>
          </div>
        </aside>

        {drawerOpen && <div className="gov-drawer-scrim gov-only-mobile" onClick={() => setDrawerOpen(false)} />}

        <main className="gov-main">
          <Active />
        </main>
      </div>
    </div>
  );
}
